import type { ChangeEvent, FocusEvent } from 'react';

import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fromCamelCaseToWords, triggerGtmEvent } from '../misc/functions';
import { updateShowColumns } from '../features/table/table.actions';
import { gtmEvents } from '../misc/gtm.constants';
import { filtersUpdated } from '../features/table/table.slice';
import { selectAutocompleteLists } from '../features/table/table.selectors';

interface FilterProps {
  name: string;
  placeholder?: string;
}

export function Filter({ name, placeholder }: FilterProps) {
  const dispatch = useAppDispatch();
  const filters = useAppSelector((state) => state.table.filters);
  const showColumns = useAppSelector((state) => state.table.showColumns);
  const autocompleteList = useAppSelector(
    (state) => selectAutocompleteLists(state)[name],
  );
  const autocompleteListName = `${fromCamelCaseToWords(name ?? '')
    .split(' ')
    .join('-')
    .toLowerCase()}-autocomplete-list`;

  function onChange(event: ChangeEvent<HTMLInputElement>) {
    if (!showColumns.includes(name)) {
      dispatch(updateShowColumns([...showColumns, name]));
    }

    dispatch(filtersUpdated({ [name]: event.target.value }));
  }

  function onBlur(event: FocusEvent<HTMLInputElement>) {
    triggerGtmEvent(gtmEvents.filterChange, {
      filter_name: name,
      filter_value: event.target.value,
    });
  }

  function resetFilter() {
    dispatch(filtersUpdated({ [name]: '' }));
  }

  return (
    <div className="filter">
      <label className="filter__title">
        <span className="filter__title-text">{placeholder}</span>
        <input
          type="text"
          name={name}
          list={autocompleteListName}
          data-qa={name}
          className={'input' + (filters[name].length ? ' input--filled' : '')}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          value={filters[name]}
        />
        {autocompleteList.length > 0 &&
          !autocompleteList.find(
            (i) => filters[name].toLowerCase() === String(i).toLowerCase(),
          ) && (
            <datalist id={autocompleteListName}>
              {autocompleteList.map((item) => (
                <option key={item} value={item} />
              ))}
            </datalist>
          )}
      </label>
      <button
        className="btn btn--danger"
        onClick={resetFilter}
        disabled={!filters[name]}
      >
        x
      </button>
    </div>
  );
}
